// react
import { useCallback, useEffect, useState } from 'react';

// components
import AvatarEditorPage from '@karehero/llama/pwa/pages/AvatarEditorPage/AvatarEditorPage';

// hooks
import { useAvatarEditor } from '@karehero/llama/pwa/hooks/useAvatarEditor';
import { useGoBack } from 'hooks/useGoBack';

// utils
import {
  avatarStyleList,
  avatarStyles,
} from '@karehero/llama/pwa/utils/avatarStyles';
import {
  buildAvatarUrl,
  buildThumbnailUrl,
} from '@karehero/llama/pwa/utils/buildAvatarUrl';

// store
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { useUpdateAccountAvatarMutation } from 'store/api/account';

/**
 * AvatarEditor view wires the avatar editor page design to Redux state and API.
 */
export const AvatarEditor = () => {
  const goBack = useGoBack();
  const account = useSelector(getAccount);
  const [updateAvatar] = useUpdateAccountAvatarMutation();
  const [isSaving, setIsSaving] = useState(false);

  const editor = useAvatarEditor();

  // Initialise from saved config on mount
  useEffect(() => {
    editor.initFromConfig(account?.avatarConfig);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    const avatarConfig = JSON.stringify({
      styleId: editor.activeStyleId,
      options: editor.currentOptions,
    });
    const avatarUrl = editor.currentUrl;

    try {
      await updateAvatar({
        accountId: account!.id,
        avatarConfig,
        avatarUrl,
      }).unwrap();
      goBack();
    } catch (err) {
      console.error('Failed to save avatar:', err);
      setIsSaving(false);
    }
  }, [
    editor.activeStyleId,
    editor.currentOptions,
    editor.currentUrl,
    updateAvatar,
    goBack,
  ]);

  const handleBuildThumbnailUrl = useCallback(
    (categoryKey: string, optionValue: string) => {
      return buildThumbnailUrl(
        editor.activeStyle,
        editor.currentOptions,
        categoryKey,
        optionValue,
      );
    },
    [editor.activeStyle, editor.currentOptions],
  );

  const handleBuildStyleSampleUrl = useCallback((styleId: string) => {
    const style = avatarStyles[styleId];
    if (!style) return '';
    return buildAvatarUrl(style, style.defaults);
  }, []);

  return (
    <AvatarEditorPage
      styles={avatarStyleList}
      activeStyleId={editor.activeStyleId}
      onStyleChange={editor.switchStyle}
      previewUrl={editor.currentUrl}
      categoryGroups={editor.categoryGroups}
      currentOptions={editor.currentOptions}
      onOptionChange={editor.setOption}
      buildThumbnailUrl={handleBuildThumbnailUrl}
      buildStyleSampleUrl={handleBuildStyleSampleUrl}
      onBack={goBack}
      onSave={handleSave}
      onRandomise={editor.randomise}
      isSaving={isSaving}
    />
  );
};

AvatarEditor.displayName = 'AvatarEditor';
export default AvatarEditor;
